import { Dispatch, SetStateAction } from 'react';

import { Flex, Form, FormListFieldData } from 'antd';
import { ColumnProps } from 'antd/es/table';

import useIsMobile from '@/hooks/useIsMobile';

import {
	DEBT_GUARANTOR_OPTIONS,
	DEBT_PURPOSE_OPTIONS,
	YES_OR_NO_TYPE_OPTIONS,
} from '@/constants/formFieldOptions';

import DeleteIcon from '@/assets/icons/upload-files/delete.svg?react';

import {
	EditableInputFormItem,
	EditableMoneyFormItem,
	EditableNumberFormItem,
	EditableSelectFormItem,
} from '../components';
import { EditableDateFromItem } from '../components/editable-date-form-item';

import { isNil } from '@/utils';

import styles from '../DebtsTable.module.scss';

type TUseGetDebtsTableColumnsProps = {
	setIsDeleteModalOpen: Dispatch<SetStateAction<number | null>>;
};

export const useGetDebtsTableColumns = ({
	setIsDeleteModalOpen,
}: TUseGetDebtsTableColumnsProps): ((
	remove: (index: number) => void,
) => ColumnProps<FormListFieldData>[]) => {
	const form = Form.useFormInstance();
	const isMobile = useIsMobile();

	const businessDebts = Form.useWatch('businessDebts', form) || [];

	const commonColumnProps = {
		align: 'left' as const,
		onHeaderCell: () => ({
			style: { textAlign: 'center' as const },
		}),
	};

	return (remove: (index: number) => void) => [
		{
			title: 'Lender/Debtor',
			dataIndex: 'lenderDebtor',
			key: 'lenderDebtor',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 170 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableInputFormItem
						itemValue={businessDebts?.[index]?.lenderName}
						name={[field.name, 'lenderName']}
					/>
				);
			},
		},
		{
			title: 'Original Date',
			dataIndex: 'fundsReceivedDate',
			key: 'fundsReceivedDate',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 140 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableDateFromItem
						itemValue={businessDebts?.[index]?.fundsReceivedDate}
						name={[field.name, 'fundsReceivedDate']}
					/>
				);
			},
		},
		{
			title: 'Original Balance or Credit Limit',
			dataIndex: 'totalFunding',
			key: 'totalFunding',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 140 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableMoneyFormItem
						itemValue={businessDebts?.[index]?.totalFunding}
						name={[field.name, 'totalFunding']}
					/>
				);
			},
		},
		{
			title: 'Loan term',
			dataIndex: 'loanTerm',
			key: 'loanTerm',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 102 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableNumberFormItem
						itemValue={businessDebts?.[index]?.loanTerm}
						name={[field.name, 'loanTerm']}
						numberInputProps={{ min: 1, max: 600 }}
					/>
				);
			},
		},
		{
			title: 'Interest Rate',
			dataIndex: 'interestRate',
			key: 'interestRate',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				const value = businessDebts?.[index]?.interestRate;
				return (
					<EditableNumberFormItem
						itemValue={!isNil(value) ? `${value}%` : ''}
						name={[field.name, 'interestRate']}
					/>
				);
			},
		},
		{
			title: 'Current Balance',
			dataIndex: 'outstandingBalance',
			key: 'outstandingBalance',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableMoneyFormItem
						itemValue={businessDebts?.[index]?.outstandingBalance}
						name={[field.name, 'outstandingBalance']}
					/>
				);
			},
		},
		{
			title: 'Required Monthly Payment',
			dataIndex: 'monthlyPayment',
			key: 'monthlyPayment',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 150 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableMoneyFormItem
						itemValue={businessDebts?.[index]?.monthlyPayment}
						name={[field.name, 'monthlyPayment']}
						required
					/>
				);
			},
		},
		{
			title: 'SBA Loan (Y/N)',
			dataIndex: 'isSBALoan',
			key: 'isSBALoan',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableSelectFormItem
						itemValue={businessDebts?.[index]?.isSBALoan}
						name={[field.name, 'isSBALoan']}
						options={YES_OR_NO_TYPE_OPTIONS}
					/>
				);
			},
		},
		{
			title: 'Refinance this Debt',
			dataIndex: 'isOpenToRefinance',
			key: 'isOpenToRefinance',
			align: 'center',
			onCell: () => ({ style: { minWidth: 110 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableSelectFormItem
						itemValue={businessDebts?.[index]?.isOpenToRefinance}
						name={[field.name, 'isOpenToRefinance']}
						options={YES_OR_NO_TYPE_OPTIONS}
					/>
				);
			},
		},
		{
			title: 'Original Purpose of Debt',
			dataIndex: 'debtPurposeType',
			key: 'debtPurposeType',
			align: 'center',
			onCell: () => ({ style: { minWidth: 110 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableSelectFormItem
						itemValue={businessDebts?.[index]?.debtPurposeType}
						name={[field.name, 'debtPurposeType']}
						options={DEBT_PURPOSE_OPTIONS}
					/>
				);
			},
		},
		{
			title: 'Specific Collateral Securing Debt',
			dataIndex: 'debtGuarantorType',
			key: 'debtGuarantorType',
			align: 'center',
			onCell: () => ({ style: { minWidth: 110 }, className: styles['debt-table-cell'] }),
			render: (_, field: FormListFieldData, index: number) => {
				return (
					<EditableSelectFormItem
						itemValue={businessDebts?.[index]?.debtGuarantorType}
						name={[field.name, 'debtGuarantorType']}
						options={DEBT_GUARANTOR_OPTIONS}
					/>
				);
			},
		},
		{
			onCell: () => ({ style: { minWidth: 56 } }),
			title: isMobile ? 'Actions' : '',
			fixed: 'right',
			dataIndex: 'action',
			key: 'action',
			render: (_, field: FormListFieldData) => {
				return (
					<Flex justify="center" style={{ width: '100%' }}>
						<DeleteIcon
							style={{ cursor: 'pointer' }}
							onClick={() => {
								const isDebt =
									businessDebts?.[field.name] &&
									Object.keys(businessDebts?.[field.name]).length > 0;

								if (isDebt) {
									setIsDeleteModalOpen(field.name);
								} else {
									remove(field.name);
								}
							}}
						/>
					</Flex>
				);
			},
		},
	];
};
